export type OrgRole = "ORG_OWNER" | "ORG_ADMIN" | "ORG_MANAGER" | "ORG_MEMBER";

export type OrgCapability =
  | "members.manage"
  | "teams.manage"
  | "billing.manage"
  | "seats.assign"
  | "workspace.enter"
  | "audit.read";

const ROLE_RANK: Record<OrgRole, number> = {
  ORG_OWNER: 0,
  ORG_ADMIN: 1,
  ORG_MANAGER: 2,
  ORG_MEMBER: 3,
};

const CAPABILITY_ROLES: Record<OrgCapability, readonly OrgRole[]> = {
  "members.manage": ["ORG_OWNER", "ORG_ADMIN"],
  "teams.manage": ["ORG_OWNER", "ORG_ADMIN"],
  "billing.manage": ["ORG_OWNER"],
  "seats.assign": ["ORG_OWNER", "ORG_ADMIN"],
  "workspace.enter": ["ORG_OWNER", "ORG_ADMIN", "ORG_MANAGER"],
  "audit.read": ["ORG_OWNER", "ORG_ADMIN"],
};

export const INVITABLE_ORG_ROLES: readonly Exclude<OrgRole, "ORG_OWNER">[] = [
  "ORG_ADMIN",
  "ORG_MANAGER",
  "ORG_MEMBER",
];

export function hasOrgCapability(
  role: string | null | undefined,
  capability: OrgCapability,
): boolean {
  return CAPABILITY_ROLES[capability].includes(role as OrgRole);
}

export function orgRoleRank(role: OrgRole): number {
  return ROLE_RANK[role];
}

export function canManageOrgMember(
  actorRole: OrgRole,
  targetRole: OrgRole,
  targetIsSelf = false,
): boolean {
  return (
    !targetIsSelf &&
    hasOrgCapability(actorRole, "members.manage") &&
    orgRoleRank(targetRole) > orgRoleRank(actorRole)
  );
}

export function canAssignOrgRole(
  actorRole: OrgRole,
  targetRole: OrgRole,
): boolean {
  return (
    hasOrgCapability(actorRole, "members.manage") &&
    orgRoleRank(targetRole) > orgRoleRank(actorRole)
  );
}

/** Organization management is reserved for owners and administrators. */
export function canEnterOrganizationContext(
  role: string | null | undefined,
): boolean {
  return hasOrgCapability(role, "teams.manage");
}

export function canEnterTeamContext(input: {
  orgRole: string | null | undefined;
  isTeamLeader?: boolean;
  isTeamManager?: boolean;
}): boolean {
  return (
    Boolean(input.orgRole) &&
    (hasOrgCapability(input.orgRole, "workspace.enter") ||
      Boolean(input.isTeamLeader) ||
      Boolean(input.isTeamManager))
  );
}
